/**
 * Storage Service - загрузка изображений в Firebase Storage
 * Путь: projects/{projectId}/images/{timestamp}_{filename}
 */
import { storage } from '../firebase';
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import imageCompression from 'browser-image-compression';

// Максимальный размер файла (3MB)
const MAX_FILE_SIZE = 3 * 1024 * 1024;

// Опции сжатия изображений
const compressionOptions = {
  maxSizeMB: 3,
  maxWidthOrHeight: 2048,
  useWebWorker: true,
  fileType: 'image/webp',
};

/**
 * Сжимает изображение если оно больше MAX_FILE_SIZE
 * @param {File} file - исходный файл
 * @param {Function} onProgress - callback прогресса (0-50 для сжатия)
 * @returns {Promise<File>} - сжатый файл
 */
async function compressImage(file, onProgress) {
  // Если файл уже маленький, не сжимаем
  if (file.size <= MAX_FILE_SIZE) {
    onProgress?.(50);
    return file;
  }

  try {
    const compressedFile = await imageCompression(file, {
      ...compressionOptions,
      onProgress: (progress) => {
        // Сжатие занимает 0-50% от общего прогресса
        onProgress?.(Math.round(progress / 2));
      },
    });

    console.log(
      `Сжатие: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(
        compressedFile.size /
        1024 /
        1024
      ).toFixed(2)}MB`
    );

    return compressedFile;
  } catch (error) {
    console.error('Ошибка сжатия:', error);
    // Если сжатие не удалось, возвращаем оригинал
    onProgress?.(50);
    return file;
  }
}

/**
 * Генерирует уникальное имя файла
 * @param {string} originalName - оригинальное имя файла
 * @returns {string} - уникальное имя
 */
function generateFileName(originalName) {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);
  const extension = originalName.includes('.')
    ? originalName.split('.').pop().toLowerCase()
    : 'webp';
  const baseName = originalName
    .replace(/\.[^/.]+$/, '')
    .replace(/[^a-zA-Z0-9-_]/g, '_')
    .substring(0, 50);

  return `${timestamp}_${randomId}_${baseName}.${extension}`;
}

/**
 * Загружает изображение в Firebase Storage
 * @param {File} file - файл для загрузки
 * @param {string} projectId - ID проекта
 * @param {Function} onProgress - callback прогресса (0-100)
 * @returns {Promise<{url: string, path: string, filename: string, size: number}>}
 */
export async function uploadImage(file, projectId, onProgress) {
  if (!file) {
    throw new Error('Файл не выбран');
  }

  if (!projectId) {
    throw new Error('Project ID не указан');
  }

  // Проверяем тип файла
  if (!file.type.startsWith('image/')) {
    throw new Error('Можно загружать только изображения');
  }

  try {
    // 1. Сжимаем изображение (0-50% прогресса)
    const compressedFile = await compressImage(file, onProgress);

    // 2. Генерируем путь и имя файла
    const filename = generateFileName(file.name);
    const storagePath = `projects/${projectId}/images/${filename}`;
    const storageRef = ref(storage, storagePath);

    // 3. Загружаем в Storage (50-100% прогресса)
    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, compressedFile, {
        contentType: compressedFile.type,
        customMetadata: {
          originalName: file.name,
          uploadedAt: new Date().toISOString(),
        },
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const uploadProgress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          // Загрузка занимает 50-100% от общего прогресса
          onProgress?.(50 + Math.round(uploadProgress / 2));
        },
        (error) => {
          console.error('Ошибка загрузки:', error);
          reject(getUploadError(error));
        },
        async () => {
          try {
            const url = await getDownloadURL(uploadTask.snapshot.ref);
            onProgress?.(100);

            resolve({
              url,
              path: storagePath,
              filename,
              size: compressedFile.size,
              mimeType: compressedFile.type,
            });
          } catch (error) {
            reject(new Error('Не удалось получить URL загруженного файла'));
          }
        }
      );
    });
  } catch (error) {
    console.error('Upload error:', error);
    throw error;
  }
}

/**
 * Удаляет изображение из Firebase Storage
 * @param {string} storagePath - путь к файлу в Storage
 */
export async function deleteImage(storagePath) {
  if (!storagePath) {
    return;
  }

  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    console.log('Изображение удалено:', storagePath);
  } catch (error) {
    // Если файл не найден, это нормально
    if (error.code === 'storage/object-not-found') {
      console.log('Файл уже удалён:', storagePath);
      return;
    }
    console.error('Ошибка удаления:', error);
    throw new Error('Не удалось удалить изображение');
  }
}

/**
 * Преобразует ошибку Firebase в понятное сообщение
 */
function getUploadError(error) {
  const errorMessages = {
    'storage/unauthorized': 'Нет прав для загрузки. Войдите в аккаунт.',
    'storage/canceled': 'Загрузка отменена',
    'storage/unknown': 'Неизвестная ошибка при загрузке',
    'storage/retry-limit-exceeded':
      'Превышено количество попыток. Попробуйте позже.',
    'storage/quota-exceeded': 'Превышена квота хранилища',
  };

  return new Error(errorMessages[error.code] || 'Ошибка загрузки файла');
}

/**
 * Загружает статический HTML в Firebase Storage
 * Используется при публикации для генерации статических страниц
 * @param {string} key - ключ для хранения (subdomain или customDomain)
 * @param {string} htmlContent - HTML контент
 * @param {string} pagePath - путь страницы (например 'about' для /about/index.html, пусто для главной)
 * @returns {Promise<{url: string, path: string}>}
 */
export async function uploadPublishedHTML(key, htmlContent, pagePath = '') {
  if (!key) {
    throw new Error('Key не указан');
  }

  if (!htmlContent) {
    throw new Error('HTML контент пустой');
  }

  // Формируем путь: главная -> /index.html, остальные -> /{slug}/index.html
  const basePath = `published/${key.toLowerCase()}`;
  const storagePath = pagePath
    ? `${basePath}/${pagePath}/index.html`
    : `${basePath}/index.html`;
  const storageRef = ref(storage, storagePath);

  try {
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });

    await uploadBytesResumable(storageRef, blob, {
      contentType: 'text/html; charset=utf-8',
      customMetadata: {
        publishedAt: new Date().toISOString(),
      },
    });

    const url = await getDownloadURL(storageRef);

    console.log(`Published HTML uploaded: ${storagePath} (${htmlContent.length} bytes)`);

    return {
      url,
      path: storagePath,
    };
  } catch (error) {
    console.error('Upload HTML error:', error);
    throw new Error('Не удалось загрузить HTML');
  }
}

/**
 * Загружает OG-превью изображение в Firebase Storage
 * @param {string} key - ключ для хранения (subdomain или customDomain)
 * @param {Blob} imageBlob - blob с изображением превью
 * @returns {Promise<{url: string, path: string}>}
 */
export async function uploadPreviewImage(key, imageBlob) {
  if (!key) {
    throw new Error('Key не указан');
  }

  if (!imageBlob) {
    throw new Error('Image blob пустой');
  }

  const storagePath = `published/${key.toLowerCase()}/og-preview.png`;
  const storageRef = ref(storage, storagePath);

  try {
    await uploadBytesResumable(storageRef, imageBlob, {
      contentType: 'image/png',
      customMetadata: {
        publishedAt: new Date().toISOString(),
      },
    });

    const url = await getDownloadURL(storageRef);

    console.log(`Preview image uploaded: ${storagePath} (${imageBlob.size} bytes)`);

    return {
      url,
      path: storagePath,
    };
  } catch (error) {
    console.error('Upload preview error:', error);
    throw new Error('Не удалось загрузить превью');
  }
}
